import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessibilityTTS } from '@/hooks/useAccessibilityTTS';
import Hangul from 'hangul-js';
import { type KeyboardReactInterface } from 'react-simple-keyboard';

export type KeyboardLanguage = 'ko' | 'en';

export interface UseKeyboardSimpleParams {
    value: string;
    keyboardLanguage: KeyboardLanguage;
    onInputChange?: (value: string) => void;
    onLanguageChange?: (language: KeyboardLanguage) => void;
}

export interface Rows {
    row1: string[];
    row2: string[];
    row3: string[];
}

const buildRows = (isKorean: boolean, isShiftPressed: boolean): Rows => {
    if (isKorean) {
        return {
            row1: isShiftPressed ? ['ㅃ', 'ㅉ', 'ㄸ', 'ㄲ', 'ㅆ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅒ', 'ㅖ'] : ['ㅂ', 'ㅈ', 'ㄷ', 'ㄱ', 'ㅅ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅐ', 'ㅔ'],
            row2: ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ'],
            row3: ['ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ'],
        };
    }
    return {
        row1: isShiftPressed ? ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'] : ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
        row2: isShiftPressed ? ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'] : ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
        row3: isShiftPressed ? ['Z', 'X', 'C', 'V', 'B', 'N', 'M'] : ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
    };
};

// Kiểm tra xem ký tự có phải là phụ âm tiếng Hàn không
const isKoreanConsonant = (char: string): boolean => {
    const consonants = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
    return consonants.includes(char);
};

// Kiểm tra xem hai phụ âm có thể kết hợp thành phụ âm kép không
const canCombineConsonants = (prevChar: string, currentChar: string): boolean => {
    // Các trường hợp phụ âm có thể ghép thành phụ âm kép
    const combinablePairs = [
        ['ㄱ', 'ㅅ'], // ㄱ + ㅅ = ㄳ
        ['ㄴ', 'ㅈ'], // ㄴ + ㅈ = ㄵ
        ['ㄴ', 'ㅎ'], // ㄴ + ㅎ = ㄶ
        ['ㄹ', 'ㄱ'], // ㄹ + ㄱ = ㄺ
        ['ㄹ', 'ㅁ'], // ㄹ + ㅁ = ㄻ
        ['ㄹ', 'ㅂ'], // ㄹ + ㅂ = ㄼ
        ['ㄹ', 'ㅅ'], // ㄹ + ㅅ = ㄽ
        ['ㄹ', 'ㅌ'], // ㄹ + ㅌ = ㄾ
        ['ㄹ', 'ㅍ'], // ㄹ + ㅍ = ㄿ
        ['ㄹ', 'ㅎ'], // ㄹ + ㅎ = ㅀ
        ['ㅂ', 'ㅅ'], // ㅂ + ㅅ = ㅄ
    ];

    return combinablePairs.some(([first, second]) => first === prevChar && second === currentChar);
};

const insertChar = (current: string, key: string, isKorean: boolean): string => {
    if (!isKorean) return `${current}${key}`;

    const lastChar = current.slice(-1);

    // Nếu key là nguyên âm
    if (Hangul.isVowel(key)) {
        // Kiểm tra ký tự cuối có phải là phụ âm đơn không
        if (isKoreanConsonant(lastChar)) {
            const isCompleteChar = lastChar.charCodeAt(0) >= 0xac00 && lastChar.charCodeAt(0) <= 0xd7a3;

            if (!isCompleteChar) {
                // Nếu là phụ âm đơn, chỉ ghép với nguyên âm này
                const before = current.slice(0, -1);
                const combined = Hangul.assemble([lastChar, key]);
                return `${before}${combined}`;
            }
        }

        // Nếu ký tự cuối là âm tiết hoàn chỉnh, thêm nguyên âm vào cuối âm tiết đó
        if (lastChar && lastChar.charCodeAt(0) >= 0xac00 && lastChar.charCodeAt(0) <= 0xd7a3) {
            const before = current.slice(0, -1);
            const jamos = Hangul.disassemble(lastChar);
            jamos.push(key);
            const combined = Hangul.assemble(jamos);
            return `${before}${combined}`;
        }

        // Trường hợp khác, thêm nguyên âm riêng lẻ
        return `${current}${key}`;
    }

    // Nếu key là phụ âm
    if (isKoreanConsonant(key)) {
        // Kiểm tra nếu ký tự cuối cũng là phụ âm đơn (không phải âm tiết hoàn chỉnh)
        if (isKoreanConsonant(lastChar)) {
            const isCompleteChar = lastChar.charCodeAt(0) >= 0xac00 && lastChar.charCodeAt(0) <= 0xd7a3;
            if (!isCompleteChar) {
                // Không cho phép ghép phụ âm đơn với phụ âm đơn - luôn thêm riêng lẻ
                return `${current}${key}`;
            }
        }

        // Nếu ký tự cuối là âm tiết hoàn chỉnh
        if (lastChar && lastChar.charCodeAt(0) >= 0xac00 && lastChar.charCodeAt(0) <= 0xd7a3) {
            const jamos = Hangul.disassemble(lastChar);
            const lastJamo = jamos[jamos.length - 1];

            // Kiểm tra xem phần tử cuối có phải là phụ âm không
            // Nếu không phải phụ âm, đó là nguyên âm (có thể là nguyên âm đôi chưa có phụ âm cuối)
            const hasFinalConsonant = isKoreanConsonant(lastJamo);

            // Nếu chưa có phụ âm cuối (phần tử cuối là nguyên âm)
            if (!hasFinalConsonant) {
                const before = current.slice(0, -1);
                jamos.push(key);
                const combined = Hangul.assemble(jamos);
                return `${before}${combined}`;
            }

            // Nếu đã có phụ âm cuối, kiểm tra xem có thể kết hợp thành phụ âm kép không
            if (hasFinalConsonant) {
                const lastConsonant = lastJamo; // Phụ âm cuối hiện tại
                if (canCombineConsonants(lastConsonant, key)) {
                    // Có thể kết hợp thành phụ âm kép
                    const before = current.slice(0, -1);
                    // Thêm phụ âm mới vào cuối để tạo thành phụ âm kép
                    jamos.push(key);
                    const combined = Hangul.assemble(jamos);
                    return `${before}${combined}`;
                }
                // Không thể kết hợp - thêm phụ âm riêng lẻ
                return `${current}${key}`;
            }
        }

        // Trường hợp khác, thêm phụ âm riêng lẻ
        return `${current}${key}`;
    }

    // Các ký tự khác (số, ký tự đặc biệt) - thêm trực tiếp
    return `${current}${key}`;
};

export const useKeyboardSimple = ({ value, keyboardLanguage, onInputChange, onLanguageChange }: UseKeyboardSimpleParams) => {
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();
    const [isKorean, setIsKorean] = useState(keyboardLanguage === 'ko');
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const keyboardRef = useRef<KeyboardReactInterface | null>(null);

    useEffect(() => {
        setIsKorean(keyboardLanguage === 'ko');
    }, [keyboardLanguage]);

    const isSearchDisabled = useMemo(() => !value, [value]);

    const handleBackspace = () => {
        const current = keyboardRef.current?.getInput() ?? value ?? '';
        const next = current.slice(0, -1);
        keyboardRef.current?.setInput(next);
        onInputChange?.(next);
    };

    const handleKeyPress = (button: string) => {
        if (button === '{bksp}') return handleBackspace();
        if (button === '{shift}') return setIsShiftPressed((s) => !s);
        if (button === '{mode}') {
            const newLanguage: KeyboardLanguage = isKorean ? 'en' : 'ko';
            onLanguageChange?.(newLanguage);
            setIsShiftPressed(false);
            return;
        }
        // Append other keys (e.g., numbers) to input
        const current = keyboardRef.current?.getInput() ?? value ?? '';
        const next = insertChar(current, button, isKorean);
        keyboardRef.current?.setInput(next);
        onInputChange?.(next);
    };

    const rows = useMemo(() => buildRows(isKorean, isShiftPressed), [isKorean, isShiftPressed]);

    const textLayout = useMemo(() => {
        const row1 = `${rows.row1.join(' ')} {bksp}`;
        const row2 = `{shift} ${rows.row2.join(' ')}`;
        const row3 = isKorean ? 'z x c v b n m' : 'z x c v b n m';
        const row3Full = `{mode} ${row3} {space}`;
        const shiftRow1 = rows.row1.join(' ');
        const shiftRow2 = rows.row2.join(' ');
        const shiftRow3 = isKorean ? 'z x c v b n m' : 'z x c v b n m';
        return { default: [row1, row2, row3Full], shift: [shiftRow1, shiftRow2, shiftRow3] };
    }, [rows, isKorean]);

    const displayMap = useMemo(
        () => ({
            '{bksp}': '',
            '{shift}': '',
            '{mode}': isKorean ? '한/영' : 'KO/EN',
            '{space}': isKorean ? '띄어쓰기' : 'BLANK',
        }),
        [isKorean]
    );

    // TTS helpers
    const getFocusTtsForKey = (key: string): string => `${key}, ${t('common.button.tts.button')}`;
    const getEnterTtsForKey = (key: string, nextValue: string): string => `${key}, ${t('shop.keyboard.enter')}, ${nextValue}`;

    const getSpaceEnterTts = (_valueAfterSpace: string): string => {
        // const spaceSpoken = t('shop.keyboard.space_spoken');
        const spaceInput = `${t('shop.keyboard.space_spoken')} ${t('shop.keyboard.enter')}`;

        // const spokenText = valueAfterSpace.replace(/ /g, ` ${spaceSpoken} `).trim();

        return `${spaceInput}`;
    };
    const getFocusTtsForLabel = (label: string): string => `${label}, ${t('common.button.tts.button')}`;
    const getEnterTtsForLabelWithValue = (label: string, nextValue: string): string => `${label}, ${t('shop.keyboard.enter')}, ${nextValue}`;
    const getSwitchTts = (label: string, isOn: boolean): string =>
        `${isOn ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected')}, ${label}, ${t('common.button.tts.switch_button')}`;
    const getModeTts = (isKo: boolean): string =>
        `${isKo ? t('shop.keyboard.mode_ko') : t('shop.keyboard.mode_en')}, ${t('shop.keyboard.mode')}, ${t('common.button.tts.switch_button')}`;

    const speakIfEnabled = (text: string) => {
        speak(text);
    };

    return {
        isKorean,
        isShiftPressed,
        setIsShiftPressed,
        keyboardRef,
        isSearchDisabled,
        rows,
        textLayout,
        displayMap,
        insertChar,
        handleBackspace,
        handleKeyPress,
        // TTS
        getFocusTtsForKey,
        getEnterTtsForKey,
        getSpaceEnterTts,
        getFocusTtsForLabel,
        getEnterTtsForLabelWithValue,
        getSwitchTts,
        getModeTts,
        speakIfEnabled,
    } as const;
};

export default useKeyboardSimple;
